import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Button, Alert, LinearProgress, Divider } from '@mui/material';
import axiosInstance from '../../services/axiosInstance';

const UploadFavicon: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoMessage, setLogoMessage] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [logoLoading, setLogoLoading] = useState(false);
  const [logoExists, setLogoExists] = useState(false);
  const [logoVersion, setLogoVersion] = useState(() => Date.now());

  useEffect(() => {
    checkLogoExists();
  }, []);

  const checkLogoExists = async () => {
    try {
      const resp = await axiosInstance.get('/api/settings/logo');
      setLogoExists(!!resp.data?.exists);
      if (resp.data?.exists) setLogoVersion(Date.now());
    } catch (e) {
      console.error('Failed to check logo:', e);
    }
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(null);
    setError(null);
    setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null);
  };

  const onLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLogoMessage(null);
    setLogoError(null);
    setLogoFile(e.target.files && e.target.files[0] ? e.target.files[0] : null);
  };

  const onUpload = async () => {
    if (!file) return;
    try {
      setLoading(true);
      setError(null);
      setMessage(null);
      const form = new FormData();
      form.append('file', file);
      const resp = await axiosInstance.post('/api/settings/favicon', form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (resp.data?.success) {
        setMessage('Favicon uppdaterad. Ladda om sidan för att se ändringen.');
      } else {
        setError(resp.data?.message || 'Kunde inte uppdatera favicon');
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Fel vid uppladdning');
    } finally {
      setLoading(false);
    }
  };

  const onLogoUpload = async () => {
    if (!logoFile) return;
    try {
      setLogoLoading(true);
      setLogoError(null);
      setLogoMessage(null);
      const form = new FormData();
      form.append('file', logoFile);
      const resp = await axiosInstance.post('/api/settings/logo', form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (resp.data?.success) {
        setLogoMessage('Logotyp uppdaterad. Ladda om sidan för att se ändringen.');
        setLogoExists(true);
        setLogoVersion(Date.now());
      } else {
        setLogoError(resp.data?.message || 'Kunde inte uppdatera logotyp');
      }
    } catch (e: any) {
      setLogoError(e?.response?.data?.message || e?.message || 'Fel vid uppladdning');
    } finally {
      setLogoLoading(false);
    }
  };

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Ladda upp favicon
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Rekommenderat: PNG 512×512 (transparent bakgrund). Systemet skalar och sparar 32×32 PNG (favicon) och 180×180 Apple Touch Icon. SVG accepteras också och rasteriseras.
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button variant="outlined" component="label">
            Välj bild (PNG/SVG/ICO)
            <input type="file" accept=".png,.svg,.ico" hidden onChange={onFileChange} />
          </Button>
          <Button variant="contained" onClick={onUpload} disabled={!file || loading}>
            Ladda upp
          </Button>
        </Box>
        {loading && <LinearProgress sx={{ mt: 2 }} />}
        {message && <Alert severity="success" sx={{ mt: 2 }}>{message}</Alert>}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2">Nuvarande filer som används:</Typography>
          <ul>
            <li>/public/favicon.png (används som favicon)</li>
            <li>/public/apple-touch-icon.png (iOS home screen)</li>
          </ul>
          <Typography variant="caption" color="text.secondary">
            Om du vill stödja klassisk .ico kan vi komplettera med ett ICO‑byggsteg senare.
          </Typography>
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Ladda upp logotyp
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Logotypen visas i huvudapplikationens header (ersätter "Athena DAM") och på delade samlingssidor. Rekommenderad höjd: 60px. Bilden skalas automatiskt för att passa i headern.
        </Typography>
        {logoExists && (
          <Box sx={{ mb: 2, p: 2, bgcolor: '#17365d', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ mb: 1, color: 'white' }}>Nuvarande logotyp:</Typography>
            <img src={`/logo.png?v=${logoVersion}`} alt="Current logo" style={{ maxHeight: '60px', display: 'block' }} />
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button variant="outlined" component="label">
            {logoExists ? 'Byt logotyp' : 'Välj logotyp'} (PNG/SVG)
            <input type="file" accept=".png,.svg,.jpg,.jpeg" hidden onChange={onLogoFileChange} />
          </Button>
          <Button variant="contained" onClick={onLogoUpload} disabled={!logoFile || logoLoading}>
            Ladda upp
          </Button>
        </Box>
        {logoLoading && <LinearProgress sx={{ mt: 2 }} />}
        {logoMessage && <Alert severity="success" sx={{ mt: 2 }}>{logoMessage}</Alert>}
        {logoError && <Alert severity="error" sx={{ mt: 2 }}>{logoError}</Alert>}

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2">Information:</Typography>
          <ul>
            <li>Logotypen sparas som /public/logo.png</li>
            <li>Visas i appens header (vänster) istället för text</li>
            <li>Visas på delade samlingssidor istället för favicon</li>
          </ul>
        </Box>
      </Paper>
    </Box>
  );
};

export default UploadFavicon;


